// ChooseCharacter phase — "Chọn nhân vật của bạn": template ("Nhân vật mẫu")
// vs custom ("Tuỳ chỉnh nhân vật"). New flow. docs/fe-state-machine.md.
//
// Both cards (flow/frameCharacterDefault.png, flow/frameCharacterCapture.png)
// are empty titled cards; we render the character preview on top via
// composeCharacterStatic (theme body + cup + head). The head is the returning
// player's stored NEUTRAL composed face if present, else the gender default icon
// (body_{m,f}_e0). "Tiếp tục" sets ctx.characterMode and routes to Identify
// (preload) → the capture hub.
//
// Bridge note (step 1 of the rebuild): both modes currently land in the existing
// Capture hub (which already does default-face play AND capture). Steps 2–3
// replace that with the dedicated Instructions / new-Capture / Review screens.

import type { CharacterMode, Phase, Theme } from "../state/types";
import { mountFrame, clearChildren } from "../ui/overlay";
import { apiBase, getConfig, gameSessionUpdate } from "../api/client";
import { getSignature } from "../crypto";
import { cupCompositeUrl } from "../assets/themeMap";
import { handName } from "../assets/genderMap";
import { requestCamera } from "../capture";
import { composeCharacterStatic } from "../game/renderer";

const FLOW = "/textures/flow";

// Card art native size (frameCharacterSelected / Default / Capture are all
// 608×1136). Lock each card to this ratio so the frames never stretch.
const CARD_ASPECT = "608 / 1136";
// Insets of the #cards band inside the panel (% of frameCharacter): top clears
// the title bar, the rest are breathing room. Dial on-device vs Figma.
const CARDS_PAD = "16% 6% 6%";
const CARDS_GAP = "6%";
// Character canvas box inside a card. Use top/left + explicit width/height
// (NOT right/bottom insets): a <canvas> is a replaced element, so with
// left+right and width:auto the browser falls back to the canvas's intrinsic
// pixel size and ignores right/bottom — which makes it overflow the card.
// width = 100 − left − right, height = 100 − top − bottom(label clearance).
const CHAR_TOP = "5%";
const CHAR_LEFT = "7%";
const CHAR_W = "86%";
const CHAR_H = "82%";

let cleanup: (() => void) | null = null;

function loadImg(src: string): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.crossOrigin = "anonymous";
    img.onload = () => resolve(img);
    img.onerror = () => reject(new Error(`image: ${src}`));
    img.src = src;
  });
}

const sharedUrl = (name: string) => `${apiBase()}/v1/shared/${name}`;

export const ChooseCharacter: Phase = {
  async enter(ctx, transition) {
    const root = document.getElementById("app")!;
    clearChildren(root);
    const theme: Theme = ctx.theme ?? 0;
    const isMale = ctx.isMale ?? true;
    const g = isMale ? "m" : "f";

    const bg = document.createElement("div");
    bg.className = "app-bg";
    bg.style.backgroundImage = "url(/textures/bg_indexA.png)";
    bg.style.backgroundColor = "#f7c7d8";

    const frame = document.createElement("div");
    frame.className = "app-frame";
    frame.style.cssText =
      "position:relative;display:flex;flex-direction:column;align-items:center;padding:3% 4%;box-sizing:border-box;gap:2%;";

    frame.innerHTML = `
      <img src="${FLOW}/LogoHeader.png" alt="" style="width:90%;object-fit:contain;flex:0 0 auto;"/>
      <div style="flex:1 1 auto;min-height:0;width:100%;display:flex;align-items:center;justify-content:center;">
        <div style="position:relative;width:100%;aspect-ratio:1500/1796;flex:0 0 auto;
          background:url(${FLOW}/frameCharacter.png) center/100% 100% no-repeat;">
          <div id="cards" style="position:absolute;inset:0;padding:${CARDS_PAD};box-sizing:border-box;
            display:flex;gap:${CARDS_GAP};align-items:center;justify-content:center;"></div>
        </div>
      </div>
      <button id="continue" disabled style="
        width:55%;background:none;border:0;padding:0;cursor:pointer;opacity:0.5;
        background-image:url(${FLOW}/buttonContinue.png);background-size:contain;background-repeat:no-repeat;
        background-position:center;aspect-ratio:612/208;transition:opacity 120ms;" aria-label="Tiếp tục"></button>
    `;

    root.appendChild(bg);
    const frameCleanup = mountFrame(root, frame);
    cleanup = () => {
      if (bg.parentElement === root) root.removeChild(bg);
      frameCleanup();
    };

    const cardsEl = frame.querySelector<HTMLElement>("#cards")!;
    const continueBtn = frame.querySelector<HTMLButtonElement>("#continue")!;

    // Two selectable cards, each locked to the card art's native ratio so the
    // frames never distort. Per-card z-stack (back → front):
    //   cardbase  — soft rounded card so an UNSELECTED card still reads as a card
    //   selbg     — frameCharacterSelected (the burst); shown only when selected,
    //               drawn BELOW the label per the design
    //   cv        — the composed character
    //   label     — frameCharacterDefault / Capture (label only), on top
    //   check     — selected badge, top-right
    const mkCard = (mode: CharacterMode, labelImg: string): HTMLButtonElement => {
      const card = document.createElement("button");
      card.dataset.mode = mode;
      card.style.cssText =
        `flex:1 1 0;min-width:0;aspect-ratio:${CARD_ASPECT};background:none;border:0;padding:0;cursor:pointer;position:relative;`;
      card.innerHTML = `
        <div class="cardbase" style="position:absolute;inset:0;background:rgba(255,255,255,0.35);border-radius:7%/4%;"></div>
        <div class="selbg" style="position:absolute;inset:0;background:url(${FLOW}/frameCharacterSelected.png) center/contain no-repeat;display:none;"></div>
        <canvas class="cv" style="position:absolute;top:${CHAR_TOP};left:${CHAR_LEFT};width:${CHAR_W};height:${CHAR_H};"></canvas>
        <div class="label" style="position:absolute;inset:0;background:url(${FLOW}/${labelImg}.png) center/contain no-repeat;pointer-events:none;"></div>
        <div class="check" style="position:absolute;top:2%;right:4%;width:16%;aspect-ratio:100/96;background:url(${FLOW}/checkIcon.png) center/contain no-repeat;display:none;"></div>
      `;
      card.addEventListener("click", () => select(mode));
      cardsEl.appendChild(card);
      return card;
    };
    const cardTemplate = mkCard("template", "frameCharacterDefault");
    const cardCustom = mkCard("custom", "frameCharacterCapture");
    const cards = [cardTemplate, cardCustom];

    let picked: CharacterMode | null = ctx.characterMode;
    const select = (mode: CharacterMode) => {
      picked = mode;
      cards.forEach((c) => {
        const on = c.dataset.mode === mode;
        // The selected-state burst (frameCharacterSelected) sits behind the
        // character; the check badge marks the top-right corner.
        c.querySelector<HTMLElement>(".selbg")!.style.display = on ? "block" : "none";
        c.querySelector<HTMLElement>(".check")!.style.display = on ? "block" : "none";
      });
      continueBtn.disabled = false;
      continueBtn.style.opacity = "1";
    };

    // --- Paint the character preview onto both cards (per client, 2026-06-12) ---
    // Left ("Nhân vật mẫu") = the DEFAULT character head for everyone — new AND
    // returning players (it's the "play with the default character" choice).
    // Right ("Tuỳ chỉnh") = the camera placeholder (iconRecapture) until the
    // player has captured a face; once they have a stored neutral head, show it.
    try {
      const config = await getConfig();
      const visual = config.characterVisual;
      const templateHeadSrc = `/textures/body_${g}${theme}_e0.png`;
      const customHeadSrc = ctx.assets.character?.neutral
        ? ctx.assets.character.neutral.startsWith("http")
          ? `${ctx.assets.character.neutral}?r=${Date.now()}`
          : `${apiBase()}${ctx.assets.character.neutral}?r=${Date.now()}`
        : `${FLOW}/iconRecapture.png`;
      const [cup, body, templateHead, customHead, hand] = await Promise.all([
        loadImg(cupCompositeUrl(theme)),
        loadImg(sharedUrl(`body_${g}${theme}_0.png`)),
        loadImg(templateHeadSrc),
        loadImg(customHeadSrc),
        // Foreground hand (optional art) — null if absent, so no hand layer.
        loadImg(sharedUrl(handName(isMale, theme))).catch(() => null),
      ]);
      const headFor: Record<CharacterMode, HTMLImageElement> = {
        template: templateHead,
        custom: customHead,
      };
      const dpr = window.devicePixelRatio || 1;
      for (const c of cards) {
        const mode = c.dataset.mode as CharacterMode;
        const cv = c.querySelector<HTMLCanvasElement>(".cv")!;
        const rect = cv.getBoundingClientRect();
        const w = Math.max(1, Math.round(rect.width * dpr));
        const h = Math.max(1, Math.round(rect.height * dpr));
        cv.width = w;
        cv.height = h;
        const c2d = cv.getContext("2d");
        // template card = the default chibi head (→ visual.headDefault); custom
        // card = the player's composed face / camera placeholder (→ visual.head).
        if (c2d)
          composeCharacterStatic(
            c2d,
            { cup, body, head: headFor[mode], hand },
            visual,
            w,
            h,
            mode === "template",
          );
      }
    } catch (err) {
      console.error("[ChooseCharacter] preview render failed", err);
    }

    if (picked) select(picked);

    // Camera permission gate (custom path only) — ask for + start the camera
    // here, and only advance to the capture scene once the stream is live.
    const showCameraCover = (): HTMLElement => {
      const cover = document.createElement("div");
      cover.style.cssText =
        "position:absolute;inset:0;z-index:40;background:#0008;display:flex;align-items:center;justify-content:center;color:#fff;font-size:16px;font-weight:bold;";
      cover.textContent = "Đang mở camera…";
      root.appendChild(cover);
      return cover;
    };
    const showCameraWarning = (): void => {
      const modal = document.createElement("div");
      modal.style.cssText =
        "position:absolute;inset:0;z-index:40;background:#0009;display:flex;align-items:center;justify-content:center;padding:24px;";
      modal.innerHTML = `
        <div style="background:#fff;color:#222;border-radius:14px;padding:20px;max-width:90%;display:flex;flex-direction:column;gap:14px;">
          <div style="font-size:16px;font-weight:bold;">Cần quyền camera</div>
          <div style="font-size:14px;line-height:1.5;">Hãy <strong>cấp quyền camera</strong> để tùy chỉnh nhân vật bằng khuôn mặt của bạn.</div>
          <button id="okcam" style="padding:12px 20px;border:0;border-radius:10px;background:#e34;color:#fff;font-size:15px;font-weight:bold;cursor:pointer;align-self:flex-end;">Đồng ý</button>
        </div>`;
      root.appendChild(modal);
      modal.querySelector<HTMLButtonElement>("#okcam")!.addEventListener("click", () => modal.remove());
    };

    continueBtn.addEventListener("click", async () => {
      if (continueBtn.disabled || !picked) return;
      ctx.characterMode = picked;
      // Cập nhật use_custom_face vào session
      if (ctx.sessionId) {
        const sig = await getSignature(ctx.sessionId);
        ctx.signature = sig;
        gameSessionUpdate(sig, picked === "custom").catch((e) =>
          console.error("[ChooseCharacter] gameSessionUpdate failed", e)
        );
      }
      if (picked === "custom") {
        // Request camera permission + start the stream before the capture scene.
        continueBtn.style.pointerEvents = "none";
        continueBtn.style.opacity = "0.6";
        const cover = showCameraCover();
        try {
          await requestCamera();
        } catch (err) {
          console.error("[ChooseCharacter] camera permission denied/failed", err);
          cover.remove();
          showCameraWarning();
          continueBtn.style.pointerEvents = "auto";
          continueBtn.style.opacity = "1";
          return;
        }
        cover.remove();
      }
      transition("Identify");
    });
  },
  exit() {
    cleanup?.();
    cleanup = null;
  },
};
